'use client';

// Khối "Đặt phòng" trong modal chi tiết căn ở /sale (công khai, khách không đăng nhập).
// Luồng: chọn ngày → báo giá (server tính) → nhập thông tin → tạo booking nháp + hiện QR → poll tới khi tiền về.
// Mọi số tiền do SERVER tính; client chỉ hiển thị. Funnel (mở/chọn ngày/nhập form/bỏ dở) gửi qua /api/sale/track,
// các bước created/pay_shown/paid do server tự ghi (xem lib/sale-booking.ts).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import qrcode from 'qrcode-generator';
import { useToast } from '@/components/Toast';
import { dateAddDays, fmtDate, fmtVND, getOttChatUrl, todayISO } from '@/lib/utils';
import { logPublicClientError } from '@/lib/client-error-logger';
import { fmtHold } from '@/lib/sale-hold';
import { clearSaleGuest, readSaleGuest, saveSaleGuest, storedStayDates } from '@/lib/sale-guest-info';
import StayDatePicker from './StayDatePicker';

export interface SaleBookingInfo {
  enabled: boolean; buildingEnabled?: Record<string, boolean>; payMode: 'payos_own' | 'payos_ledifa' | 'vietqr'; amountMode: 'full' | 'deposit';
  depositPercent: number; holdMinutes: number; discountEnabled: boolean; discountPercent: number; note?: string;
}
interface Quote {
  nights: { date: string; price: number }[]; nightCount: number; subtotal: number; discount: number; discountPercent: number; total: number; due: number;
  listTotal: number; savings: number; savingsPercent: number; // giá niêm yết Airbnb cho cùng các đêm (0 = không so sánh)
  extraGuests: number; extraGuestFee: number; extraGuestFeePerNight: number; maxAdults: number; maxGuests: number; // phụ thu người lớn vượt sức chứa (đã nằm trong total)
  payMode: 'payos_own' | 'payos_ledifa' | 'vietqr';  // cách nhận tiền THEO TOÀ của căn đang xem
  booking?: SaleBookingInfo;                         // cấu hình HIỆU LỰC của toà chứa căn (cọc/giữ chỗ/giảm giá/chính sách có thể khác mặc định chung)
}
interface Payment {
  mode: 'payos_own' | 'payos_ledifa' | 'vietqr'; amountDue: number; totalAmount: number; transferContent: string;
  checkoutUrl?: string; qrPayload?: string; qrImage?: string; bank?: { bankName?: string; accountNo?: string; accountName?: string }; expiresAtMs: number;
}
type Stage = 'closed' | 'dates' | 'form' | 'payment' | 'paid' | 'conflict' | 'held';

const ERR: Record<string, string> = {
  unavailable: 'Căn này không còn trống trong khoảng ngày đã chọn. Vui lòng chọn ngày khác.',
  past_date: 'Ngày nhận phòng không được ở quá khứ.',
  invalid_dates: 'Ngày trả phòng phải sau ngày nhận phòng.',
  booking_disabled: 'Toà này chưa mở đặt phòng online — vui lòng bấm "Gọi lại cho tôi" hoặc liên hệ trực tiếp.',
  min_stay: 'Căn này yêu cầu ở tối thiểu {n} đêm.',
  max_stay: 'Căn này cho ở tối đa {n} đêm.',
  too_many_guests: 'Căn này chỉ nhận tối đa {n} khách người lớn.',
  no_price: 'Căn này chưa có giá để đặt online — vui lòng liên hệ trực tiếp.',
  rate_limited: 'Bạn thao tác quá nhanh, vui lòng thử lại sau ít phút.',
  invalid_guest: 'Vui lòng nhập đúng họ tên và số điện thoại (email nếu điền phải hợp lệ).',
  far_future: 'Chỉ nhận đặt phòng trong vòng khoảng 18 tháng tới. Vui lòng chọn ngày gần hơn.',
  too_many_pending: 'Bạn đang có đặt phòng chờ thanh toán. Vui lòng thanh toán, hoặc bấm "Gọi lại cho tôi" để nhân viên hỗ trợ, trước khi đặt thêm.',
  duplicate_pending: 'Bạn đã có đặt phòng chờ thanh toán cho căn và ngày này. Vui lòng hoàn tất thanh toán, hoặc bấm "Gọi lại cho tôi" để nhân viên hỗ trợ.',
  busy: 'Hệ thống đang có nhiều đặt phòng chờ xử lý. Vui lòng thử lại sau ít phút hoặc bấm "Gọi lại cho tôi".',
};

/** Ảnh QR từ chuỗi VietQR — vẽ SVG ngay trên client, không phụ thuộc dịch vụ ngoài. */
function QrSvg({ value }: Readonly<{ value: string }>) {
  const svg = useMemo(() => {
    try {
      const q = qrcode(0, 'M');
      q.addData(value, 'Byte');
      q.make();
      const n = q.getModuleCount(), m = 2;
      let d = '';
      for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (q.isDark(r, c)) d += `M${c + m} ${r + m}h1v1h-1z`;
      return { d, size: n + m * 2 };
    } catch { return null; }
  }, [value]);
  if (!svg) return <div className="text-xs text-rose-600">Không tạo được mã QR — dùng thông tin chuyển khoản bên dưới.</div>;
  return (
    <svg viewBox={`0 0 ${svg.size} ${svg.size}`} className="w-52 h-52 bg-white rounded-xl p-2 mx-auto" shapeRendering="crispEdges" role="img" aria-label="Mã QR thanh toán">
      <path d={svg.d} fill="#000" />
    </svg>
  );
}

/** Khung giải thích thời gian giữ chỗ — dùng ở bước điền form và màn QR. `until` chỉ có sau khi đã tạo booking. */
function HoldNotice({ hold, until, contactPhone, transfer }: Readonly<{ hold: string; until?: string; contactPhone?: string | null; transfer: boolean }>) {
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs space-y-1 text-[#4a3d30]">
      <div className="font-semibold text-amber-800">⏳ Phòng được giữ cho bạn {hold}{until ? ` — đến ${until}` : ' kể từ lúc bấm đặt'}</div>
      <ul className="list-disc pl-4 space-y-0.5">
        <li>Thanh toán <b>trong {hold}</b> để đặt phòng được xác nhận.</li>
        <li>Quá {hold} chưa thanh toán, <b>phòng sẽ được mở lại cho khách khác</b> đặt.</li>
        {transfer
          ? <li>Chuyển khoản xong, nhân viên sẽ kiểm tra và xác nhận (không tức thì) rồi liên hệ qua số điện thoại bạn đã nhập.</li>
          : <li>Thanh toán xong, đặt phòng được xác nhận tự động trong vài giây.</li>}
        {until && <li>Nếu bạn <b>đã chuyển khoản mà quá hạn</b>, hãy liên hệ ngay để chúng tôi xử lý (giữ phòng hoặc hoàn tiền).</li>}
      </ul>
      {until && contactPhone && (
        <div className="pt-1">Liên hệ: <a className="underline font-semibold" href={`tel:${contactPhone.replace(/\s/g, '')}`}>{contactPhone}</a>
          {' · '}<a className="underline" href={getOttChatUrl('zalo', contactPhone)} target="_blank" rel="noreferrer">Zalo</a></div>
      )}
    </div>
  );
}

/** Nút "Gọi lại cho tôi": khách để lại SĐT → server báo nhóm Telegram Vận hành để NV gọi lại trao đổi về booking. */
function CallbackBox({ roomId, getSession, defaults, contactPhone }: Readonly<{
  roomId: string; getSession: () => string; contactPhone?: string | null;
  defaults: { phone: string; name: string; from: string; to: string; adults: number; bookingId?: string };
}>) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [state, setState] = useState<'idle' | 'sent' | 'unsent'>('idle');
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');
  const lastSelfCall = useRef<Record<string, number>>({});
  const inputCls = 'w-full border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm';

  // Khách CHỦ ĐỘNG gọi/copy SĐT tenant → báo nhóm Vận hành (nghe máy). Chống báo trùng: mỗi hành động 1 lần / 60s / phiên.
  const reportSelfCall = (action: 'call' | 'copy') => {
    const now = Date.now();
    if (now - (lastSelfCall.current[action] ?? 0) < 60_000) return;
    lastSelfCall.current[action] = now;
    fetch('/api/sale/self-call', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
      body: JSON.stringify({ roomId, action, phone: phone || defaults.phone, name: name || defaults.name, from: defaults.from, to: defaults.to, bookingId: defaults.bookingId, sessionId: getSession() }),
    }).catch(e => logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_self_call', message: e instanceof Error ? e.message : String(e) }));
  };
  const copyTenantPhone = () => {
    if (!contactPhone) return;
    reportSelfCall('copy');
    navigator.clipboard.writeText(contactPhone).then(
      () => setCopied(true),
      () => setErr('Không copy được — bạn có thể bấm vào số để gọi.'),
    );
  };

  const toggle = () => {
    if (!open) { const sv = readSaleGuest(); setPhone(p => p || defaults.phone || sv.phone || ''); setName(n => n || defaults.name || sv.name || ''); }
    setOpen(o => !o);
  };

  const send = async () => {
    if (sending) return;
    setSending(true); setErr('');
    try {
      const res = await fetch('/api/sale/callback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, phone, name, from: defaults.from, to: defaults.to, adults: defaults.adults, bookingId: defaults.bookingId, sessionId: getSession() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status >= 500 || !j.error) throw new Error(`callback ${res.status}`);
        setErr(j.error === 'invalid_phone' ? 'Vui lòng nhập số điện thoại hợp lệ.' : j.error === 'rate_limited' ? 'Bạn đã gửi nhiều yêu cầu, vui lòng thử lại sau ít phút.' : 'Không gửi được yêu cầu, vui lòng thử lại.');
        return;
      }
      saveSaleGuest({ phone, name }); // SĐT/tên vừa nhập ở đây cũng dùng lại cho form đặt phòng & các nơi khác
      setState(j.notified ? 'sent' : 'unsent');
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_callback', message: e instanceof Error ? e.message : String(e) });
      setErr('Không gửi được yêu cầu — mất mạng hoặc hệ thống lỗi. Vui lòng thử lại.');
    } finally { setSending(false); }
  };

  if (state === 'sent') return <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">✅ Đã gửi yêu cầu. Nhân viên sẽ gọi lại số <b>{phone}</b> để trao đổi với bạn về đặt phòng.</div>;
  if (state === 'unsent') {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
        Yêu cầu của bạn đã được lưu nhưng chưa thông báo được cho nhân viên ngay.{contactPhone ? <> Vui lòng gọi trực tiếp <a className="underline font-semibold" href={`tel:${contactPhone.replace(/\s/g, '')}`}>{contactPhone}</a>.</> : ' Vui lòng thử lại sau ít phút.'}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={toggle} className="text-sm font-medium underline text-[#4a3d30]">📞 Gọi lại cho tôi</button>
        {contactPhone && (
          <span className="text-xs text-[#8a7a68] flex items-center gap-1.5">
            hoặc tự gọi:
            <a href={`tel:${contactPhone.replace(/\s/g, '')}`} onClick={() => reportSelfCall('call')} className="font-semibold text-[#4a3d30] underline">{contactPhone}</a>
            <button type="button" onClick={copyTenantPhone} className="px-1.5 py-0.5 rounded border border-[#e4ddd4] text-[11px] text-[#4a3d30]">{copied ? 'Đã copy ✓' : 'Copy'}</button>
          </span>
        )}
      </div>
      {open && (
        <div className="rounded-lg border border-[#e4ddd4] p-3 space-y-2">
          <div className="text-xs text-[#8a7a68]">Để lại số điện thoại, nhân viên sẽ gọi lại trao đổi với bạn về đặt phòng.{phone && defaults.phone === phone ? ' Số điện thoại điền sẵn từ thông tin bạn đã nhập — sửa nếu chưa đúng.' : ''}</div>
          <input className={inputCls} inputMode="tel" placeholder="Số điện thoại / Zalo *" value={phone} onChange={e => setPhone(e.target.value)} />
          <input className={inputCls} placeholder="Tên của bạn (không bắt buộc)" value={name} onChange={e => setName(e.target.value)} />
          {err && <div className="text-xs text-rose-600">{err}</div>}
          <button type="button" onClick={send} disabled={sending || phone.replace(/\D/g, '').length < 8}
            className="w-full py-2 rounded-xl bg-[#1c1a16] text-white text-sm font-medium disabled:opacity-50">{sending ? 'Đang gửi…' : 'Gửi yêu cầu gọi lại'}</button>
        </div>
      )}
    </div>
  );
}

export default function SaleBookingPanel({ roomId, info, contactPhone, searchRange }: Readonly<{ roomId: string; info: SaleBookingInfo; contactPhone?: string | null; searchRange?: { from: string; to: string } | null }>) {
  const { toast } = useToast();
  const [stage, setStage] = useState<Stage>('closed');
  // Điền sẵn từ những gì khách ĐÃ NHẬP trước đó (lưu trên thiết bị) → ngày đã chọn lần trước, ngày khách lọc ở trang, rồi mới tới mặc định
  const [saved] = useState(() => readSaleGuest());
  const [from, setFrom] = useState(() => storedStayDates(saved)?.from ?? (searchRange && searchRange.from >= todayISO() ? searchRange.from : dateAddDays(todayISO(), 1)));
  const [to, setTo] = useState(() => storedStayDates(saved)?.to ?? (searchRange && searchRange.from >= todayISO() ? searchRange.to : dateAddDays(todayISO(), 2)));
  const [adults, setAdults] = useState(() => saved.adults ?? 2);
  const [children, setChildren] = useState(() => saved.children ?? 0);
  const [quote, setQuote] = useState<Quote | null>(null);
  const eff: SaleBookingInfo = quote?.booking ?? info; // cấu hình theo TOÀ của căn (catalog chỉ có mặc định chung)
  const [quoteErr, setQuoteErr] = useState('');
  const [maxAdults, setMaxAdults] = useState(6);
  const [guestCapNote, setGuestCapNote] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [guest, setGuest] = useState(() => ({ name: saved.name ?? '', phone: saved.phone ?? '', email: saved.email ?? '', nationality: saved.nationality ?? '', note: '' }));
  const prefilled = !!(saved.name || saved.phone || saved.email || saved.nationality);
  const [hp, setHp] = useState(''); // honeypot chống bot — người thật không thấy/không điền
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [payment, setPayment] = useState<Payment | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now()); // đồng hồ đếm ngược giữ chỗ ở màn thanh toán
  const [claim, setClaim] = useState<'idle' | 'sending' | 'sent' | 'unsent'>('idle'); // VietQR: khách báo đã chuyển khoản
  const [bookingId, setBookingId] = useState('');
  const accessKey = useRef('');
  const sessionId = useRef('');
  const stageRef = useRef<Stage>('closed');
  const formTracked = useRef(false);
  const pollErrLogged = useRef(false);
  useEffect(() => { stageRef.current = stage; }, [stage]);
  const quoting_ = stage === 'dates' || stage === 'form'; // chỉ báo giá khi đang ở bước chọn ngày/điền form

  const getSession = useCallback(() => {
    if (!sessionId.current) sessionId.current = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return sessionId.current;
  }, []);

  const track = useCallback((event: string, step?: string, beacon = false) => {
    const body = JSON.stringify({ sessionId: getSession(), events: [{ event, roomId, step }] });
    try {
      if (beacon && navigator.sendBeacon) navigator.sendBeacon('/api/sale/track', new Blob([body], { type: 'application/json' }));
      else {
        fetch('/api/sale/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
          .catch(e => logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_track', message: e instanceof Error ? e.message : String(e) })); // log phụ: lỗi chỉ ghi nhận, không ảnh hưởng đặt phòng
      }
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_track', message: e instanceof Error ? e.message : String(e) });
    }
  }, [roomId, getSession]);

  // Bỏ dở: đóng modal/chuyển trang khi đang giữa các bước (chưa thanh toán xong)
  useEffect(() => {
    const abandon = () => {
      const s = stageRef.current;
      if (s === 'dates' || s === 'form' || s === 'payment') track('abandon', s, true);
    };
    window.addEventListener('pagehide', abandon);
    return () => { window.removeEventListener('pagehide', abandon); abandon(); };
  }, [track]);

  const open = () => { setStage('dates'); track('open'); };

  // Báo giá tự động khi đổi ngày/số khách (debounce)
  useEffect(() => {
    if (!quoting_) return;
    if (!from || !to || to <= from) { setQuote(null); setQuoteErr(ERR.invalid_dates); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(() => {
      fetch('/api/sale/book/quote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ roomId, from, to, adults, children }) })
        .then(r => r.json().then(j => ({ status: r.status, j })))
        .then(({ status, j }) => {
          if (cancelled) return;
          if (status >= 500 || (status >= 400 && !j.error)) throw new Error(`quote ${status}`);
          if (j.ok) {
            setQuote(j); setQuoteErr(''); setMaxAdults(j.maxGuests ?? 6);
            if (stageRef.current === 'dates') setStage('form');
            track('dates');
          } else if (j.error === 'too_many_guests' && Number.isInteger(j.detail) && j.detail >= 1) {
            // Số khách vượt sức chứa → tự đưa về mức tối đa để form thanh toán hiện ra ngay, khách không phải tự dò lỗi
            setMaxAdults(j.detail);
            setGuestCapNote(`Căn này chỉ nhận tối đa ${j.detail} khách người lớn — đã tự chỉnh về ${j.detail}. Nếu đi đông hơn, bấm "Gọi lại cho tôi" để nhân viên tư vấn thêm căn.`);
            setAdults(j.detail);
          } else {
            setQuote(null);
            setQuoteErr((ERR[j.error] ?? 'Không báo giá được, vui lòng thử lại.').replace('{n}', String(j.detail ?? '')));
            if (j.error === 'unavailable') track('unavailable');
            if (stageRef.current === 'form') setStage('dates');
          }
        })
        .catch(e => {
          if (cancelled) return;
          logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_quote', message: e instanceof Error ? e.message : String(e) });
          setQuote(null); setQuoteErr('Không báo giá được — mất mạng hoặc hệ thống lỗi. Vui lòng thử lại.');
        })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [from, to, adults, children, roomId, quoting_]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nhớ lại mọi thứ khách vừa nhập để các nơi khác (Gọi lại, Đặt lịch xem căn, lần mở sau) dùng lại — ghi chú tự do thì không lưu
  useEffect(() => {
    if (stage === 'closed') return;
    saveSaleGuest({ name: guest.name, phone: guest.phone, email: guest.email, nationality: guest.nationality, from, to, adults, children });
  }, [stage, guest.name, guest.phone, guest.email, guest.nationality, from, to, adults, children]);

  const forgetMe = () => {
    clearSaleGuest();
    setGuest({ name: '', phone: '', email: '', nationality: '', note: guest.note });
    toast('Đã xoá thông tin đã lưu trên thiết bị này');
  };

  const touchForm = () => { if (!formTracked.current) { formTracked.current = true; track('form'); } };

  const submit = async () => {
    if (submitting || !quote) return;
    setSubmitting(true); setFormErr('');
    try {
      const res = await fetch('/api/sale/book/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, from, to, adults, children, sessionId: sessionId.current || undefined, hp, guest }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.ok) {
        if (res.status >= 500 || !j.error) throw new Error(`create ${res.status}`);
        setFormErr((ERR[j.error] ?? 'Không đặt được phòng, vui lòng thử lại.').replace('{n}', String(j.detail ?? '')));
        if (j.error === 'unavailable') setStage('dates');
        return;
      }
      setBookingId(j.bookingId);
      if (!j.payment) { setStage('held'); return; } // đã giữ chỗ nhưng chưa sinh được QR → NV liên hệ
      accessKey.current = j.accessKey;
      setPayment(j.payment); setStage('payment');
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_create', message: e instanceof Error ? e.message : String(e) });
      setFormErr('Không đặt được phòng — mất mạng hoặc hệ thống lỗi. Vui lòng thử lại.');
    } finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (stage !== 'payment') return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stage]);

  // Poll trạng thái thanh toán
  useEffect(() => {
    if (stage !== 'payment' || !bookingId) return;
    const id = setInterval(() => {
      fetch(`/api/sale/book/status?bookingId=${encodeURIComponent(bookingId)}&key=${encodeURIComponent(accessKey.current)}`)
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (j?.status === 'paid') setStage('paid'); else if (j?.status === 'conflict') setStage('conflict'); })
        .catch(e => {
          // Poll lỗi mạng tạm thời: lần sau thử lại, không báo khách — nhưng ghi nhận 1 lần/phiên để thấy nếu lỗi kéo dài
          if (pollErrLogged.current) return;
          pollErrLogged.current = true;
          logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_poll', message: e instanceof Error ? e.message : String(e) });
        });
    }, 5000);
    return () => clearInterval(id);
  }, [stage, bookingId]);

  const callback = (
    <CallbackBox roomId={roomId} getSession={getSession} contactPhone={contactPhone}
      defaults={{ phone: guest.phone, name: guest.name, from, to, adults, bookingId: bookingId || undefined }} />
  );

  // VietQR không tự xác nhận được → khách bấm "Tôi đã chuyển khoản" để NV kiểm tra sao kê ngay (không phải chờ NV tự để ý)
  const claimTransfer = async () => {
    if (claim === 'sending' || claim === 'sent') return;
    setClaim('sending');
    try {
      const res = await fetch('/api/sale/book/transferred', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId, key: accessKey.current, sessionId: getSession() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(`transferred ${res.status}`);
      setClaim(j.notified ? 'sent' : 'unsent');
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_transferred', message: e instanceof Error ? e.message : String(e) });
      setClaim('idle');
      toast('Chưa gửi được thông báo — vui lòng thử lại hoặc bấm "Gọi lại cho tôi"', 'error');
    }
  };

  const copy = (text: string, label: string) => navigator.clipboard.writeText(text).then(() => toast(`Đã copy ${label}`), () => toast('Không copy được', 'error'));

  if (stage === 'closed') {
    return (
      <div className="space-y-2">
        <button type="button" onClick={open} className="w-full text-left rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3">
          <div className="text-sm font-bold">🛎️ Đặt phòng ngay</div>
          <div className="text-xs opacity-90">
            {eff.discountEnabled ? `Giảm ${eff.discountPercent}% khi đặt & thanh toán online · ` : ''}Chuyển khoản qua QR, xác nhận ngay
          </div>
        </button>
        {callback}
      </div>
    );
  }

  const inputCls = 'w-full border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm';
  const holdUntil = payment ? new Date(payment.expiresAtMs).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '';

  if (stage === 'paid') {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-1 text-sm text-gray-800">
        <div className="font-bold text-emerald-800">✅ Đã nhận thanh toán — đặt phòng thành công</div>
        <div>Mã booking: <b style={{ fontFamily: 'ui-monospace, monospace' }}>{bookingId}</b></div>
        <div className="text-emerald-800">{fmtDate(from)} → {fmtDate(to)}. Đội vận hành sẽ liên hệ hướng dẫn nhận phòng.</div>
      </div>
    );
  }
  if (stage === 'conflict') {
    return (
      <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 space-y-1 text-sm text-gray-800">
        <div className="font-bold text-rose-700">Rất tiếc — phòng vừa được khách khác đặt trước</div>
        <div>Chúng tôi đã nhận khoản chuyển khoản của bạn và sẽ liên hệ ngay để đổi phòng hoặc hoàn tiền. Mã tham chiếu: <b>{bookingId}</b></div>
        {callback}
      </div>
    );
  }
  if (stage === 'held') {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-1 text-sm text-gray-800">
        <div className="font-bold text-amber-800">Đã giữ chỗ cho bạn</div>
        <div>Mã booking: <b>{bookingId}</b>. Đội vận hành sẽ liên hệ qua số điện thoại bạn đã nhập để hướng dẫn thanh toán — phòng được giữ {fmtHold(eff.holdMinutes)}, quá hạn chưa thanh toán phòng sẽ được mở lại.</div>
        {callback}
      </div>
    );
  }

  if (stage === 'payment' && payment) {
    const isTransfer = payment.mode === 'vietqr';
    const remaining = payment.expiresAtMs - nowMs;
    if (remaining <= 0) {
      // Hết thời gian giữ chỗ: ẩn QR (link PayOS cũng đã hết hạn) — nếu khách ĐÃ chuyển khoản thì poll vẫn bắt được và đổi sang màn "đã thanh toán"
      return (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2 text-sm text-gray-800">
          <div className="font-bold text-amber-800">⌛ Đã hết thời gian giữ chỗ ({fmtHold(eff.holdMinutes)})</div>
          <div>Phòng đã được mở lại cho khách khác. Nếu bạn <b>đã chuyển khoản</b>, vui lòng liên hệ ngay{contactPhone ? <> <a className="underline font-semibold" href={`tel:${contactPhone.replace(/\s/g, '')}`}>{contactPhone}</a></> : ''} để chúng tôi xử lý (mã booking <b>{bookingId}</b>). Nếu chưa, bạn có thể đặt lại.</div>
          <button type="button" onClick={() => { setPayment(null); setBookingId(''); setClaim('idle'); setStage('dates'); }}
            className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">Đặt lại</button>
          {callback}
        </div>
      );
    }
    const mm = String(Math.floor(remaining / 60_000)).padStart(2, '0'), ss = String(Math.floor((remaining % 60_000) / 1000)).padStart(2, '0');
    return (
      <div className="rounded-xl border border-emerald-300 p-4 space-y-3">
        <div className="text-sm font-bold">Quét QR để thanh toán <span className={`ml-1 font-mono text-xs ${remaining < 5 * 60_000 ? 'text-rose-600' : 'text-[#8a7a68]'}`}>⏳ còn {mm}:{ss}</span></div>
        {payment.qrImage
          // eslint-disable-next-line @next/next/no-img-element
          ? <img src={payment.qrImage} alt="Mã QR thanh toán" className="w-52 h-52 mx-auto rounded-xl bg-white p-2" />
          : payment.qrPayload ? <QrSvg value={payment.qrPayload} /> : null}
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-700">{fmtVND(payment.amountDue)}</div>
          {payment.amountDue < payment.totalAmount && <div className="text-xs text-[#8a7a68]">đặt cọc · tổng tiền {fmtVND(payment.totalAmount)}, phần còn lại thanh toán khi nhận phòng</div>}
        </div>
        {isTransfer && payment.bank && (
          <div className="text-xs space-y-1 bg-[#f7f4ee] rounded-lg p-3">
            <div>Ngân hàng: <b>{payment.bank.bankName}</b></div>
            <div className="flex items-center gap-2">Số TK: <b>{payment.bank.accountNo}</b> <button type="button" className="underline text-[#8a7a68]" onClick={() => copy(payment.bank!.accountNo ?? '', 'số tài khoản')}>copy</button></div>
            <div>Chủ TK: <b>{payment.bank.accountName}</b></div>
          </div>
        )}
        <HoldNotice hold={fmtHold(eff.holdMinutes)} until={holdUntil} contactPhone={contactPhone} transfer={isTransfer} />
        <div className="text-xs bg-[#f7f4ee] rounded-lg p-3 space-y-1">
          <div className="flex items-center gap-2">Nội dung chuyển khoản (giữ NGUYÊN): <b style={{ fontFamily: 'ui-monospace, monospace' }}>{payment.transferContent}</b>
            <button type="button" className="underline text-[#8a7a68]" onClick={() => copy(payment.transferContent, 'nội dung chuyển khoản')}>copy</button></div>
          <div>Mã booking: <b>{bookingId}</b></div>
        </div>
        {isTransfer && (claim === 'sent' || claim === 'unsent'
          ? <div className="text-xs rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-800">
              {claim === 'sent' ? '✅ Đã báo nhân viên kiểm tra sao kê. Khi nhận được tiền, nhân viên sẽ xác nhận booking và liên hệ bạn.' : 'Đã ghi nhận, nhưng chưa thông báo được cho nhân viên ngay — vui lòng bấm "Gọi lại cho tôi" bên dưới hoặc gọi trực tiếp.'}
            </div>
          : <button type="button" onClick={claimTransfer} disabled={claim === 'sending'}
              className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
              {claim === 'sending' ? 'Đang gửi…' : '✅ Tôi đã chuyển khoản'}
            </button>)}
        {payment.checkoutUrl && <a href={payment.checkoutUrl} target="_blank" rel="noreferrer" className="block text-center text-sm underline text-emerald-700">Mở trang thanh toán</a>}
        {callback}
      </div>
    );
  }

  const nights = quote?.nightCount ?? 0;
  return (
    <div className="rounded-xl border border-emerald-300 p-4 space-y-3">
      <div className="text-sm font-bold">🛎️ Đặt phòng</div>
      <StayDatePicker roomId={roomId} from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-[#8a7a68]">Người lớn
          <select className={inputCls} value={adults} onChange={e => { setGuestCapNote(''); setAdults(Number(e.target.value)); }}>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].filter(n => n <= maxAdults || n === adults).map(n => <option key={n} value={n}>{n}</option>)}</select>
        </label>
        <label className="text-xs text-[#8a7a68]">Trẻ em
          <select className={inputCls} value={children} onChange={e => setChildren(Number(e.target.value))}>{[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}</select>
        </label>
      </div>
      {guestCapNote && <div className="text-xs text-amber-700">{guestCapNote}</div>}
      {quote && quote.extraGuests > 0 && !quoteErr && (
        <div className="text-xs text-amber-700">Căn này tiêu chuẩn {quote.maxAdults} khách người lớn — khách thứ {quote.maxAdults + 1} trở đi tính phụ thu {fmtVND(quote.extraGuestFeePerNight)}/người/đêm (không áp giảm giá), đã cộng vào tổng bên dưới.</div>
      )}
      {quote && quote.extraGuestFeePerNight > 0 && quote.extraGuests === 0 && quote.maxGuests > quote.maxAdults && !quoteErr && (
        <div className="text-[11px] text-[#8a7a68]">Căn tiêu chuẩn {quote.maxAdults} khách, nhận thêm tối đa {quote.maxGuests - quote.maxAdults} người với phụ thu {fmtVND(quote.extraGuestFeePerNight)}/người/đêm.</div>
      )}
      {quoting && <div className="text-xs text-[#8a7a68]">Đang kiểm tra phòng trống…</div>}
      {quoteErr && !quoting && <div className="text-xs text-rose-600">{quoteErr}</div>}

      {quote && !quoteErr && (
        <div className="text-sm space-y-1 bg-[#f7f4ee] rounded-lg p-3">
          {quote.listTotal > 0 ? (
            <div className="flex justify-between text-[#8a7a68]"><span>Giá niêm yết ({nights} đêm)</span><span className="line-through">{fmtVND(quote.listTotal)}</span></div>
          ) : (
            <div className="flex justify-between"><span>{nights} đêm</span><span>{fmtVND(quote.subtotal)}</span></div>
          )}
          {quote.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Giảm {quote.discountPercent}% khi đặt trực tiếp và thanh toán online</span><span>−{fmtVND(quote.discount)}</span></div>}
          {quote.extraGuestFee > 0 && (
            <div className="flex justify-between text-[#4a3d30]">
              <span>Phụ thu thêm {quote.extraGuests} khách ({fmtVND(quote.extraGuestFeePerNight)} × {quote.extraGuests} người × {nights} đêm)</span>
              <span>+{fmtVND(quote.extraGuestFee)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold border-t border-[#e4ddd4] pt-1"><span>{quote.listTotal > 0 ? 'Giá đặt trực tiếp' : 'Tổng cộng'}</span><span>{fmtVND(quote.total)}</span></div>
          {quote.due < quote.total && <div className="flex justify-between text-amber-700 text-xs"><span>Đặt cọc ngay ({eff.depositPercent}%)</span><span>{fmtVND(quote.due)}</span></div>}
        </div>
      )}

      {stage === 'form' && quote && (
        <div className="space-y-2">
          {prefilled && (
            <div className="text-[11px] text-[#8a7a68]">
              Đã điền sẵn từ thông tin bạn nhập trước đó — kiểm tra và sửa nếu chưa đúng. <button type="button" className="underline" onClick={forgetMe}>Xoá thông tin đã lưu</button>
            </div>
          )}
          <input tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={hp} onChange={e => setHp(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
          <input className={inputCls} placeholder="Họ tên *" value={guest.name} onChange={e => { touchForm(); setGuest(g => ({ ...g, name: e.target.value })); }} />
          <input className={inputCls} inputMode="tel" placeholder="Số điện thoại / Zalo *" value={guest.phone} onChange={e => { touchForm(); setGuest(g => ({ ...g, phone: e.target.value })); }} />
          <input className={inputCls} inputMode="email" placeholder="Email (không bắt buộc)" value={guest.email} onChange={e => { touchForm(); setGuest(g => ({ ...g, email: e.target.value })); }} />
          <input className={inputCls} placeholder="Quốc tịch (không bắt buộc)" value={guest.nationality} onChange={e => { touchForm(); setGuest(g => ({ ...g, nationality: e.target.value })); }} />
          <textarea className={inputCls} rows={2} maxLength={500} placeholder="Ghi chú (giờ đến, yêu cầu đặc biệt…)" value={guest.note} onChange={e => { touchForm(); setGuest(g => ({ ...g, note: e.target.value })); }} />
          {eff.note && <div className="text-[11px] text-[#8a7a68] whitespace-pre-line">{eff.note}</div>}
          {formErr && <div className="text-xs text-rose-600">{formErr}</div>}
          <HoldNotice hold={fmtHold(eff.holdMinutes)} contactPhone={contactPhone} transfer={(quote?.payMode ?? eff.payMode) === 'vietqr'} />
          <button type="button" onClick={submit} disabled={submitting || !guest.name.trim() || !guest.phone.trim()}
            className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
            {submitting ? 'Đang tạo đặt phòng…' : `Đặt phòng & thanh toán ${fmtVND(quote.due)}`}
          </button>
        </div>
      )}
      {callback}
    </div>
  );
}
